import { FunctionComponent, ReactNode, useState } from "react";
import { Badge, Button, Form, FormInstance, Tag, Typography, theme } from "antd";
import { DownOutlined, SearchOutlined, UpOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { InputSearchFieldDef } from "./AdminSearchFilter";
import { APPLICATION_DATE_FORMAT } from "../../utils/FormUtils";

const { Text } = Typography;

export type AdminFilterPanelProps = {
    /** Instance du formulaire de recherche, pour lire les valeurs actives. */
    form: FormInstance;
    /** Définitions des champs affichés, utilisées pour libeller les filtres actifs. */
    filterDefs: InputSearchFieldDef[];
    /** Champs qu'un filtre actif ne doit jamais faire apparaître (ex. la période, toujours saisie). */
    ignoredNames?: string[];
    onClearFilter: (name: string) => void;
    children: ReactNode;
};

/** Rend lisible la valeur d'un filtre : libellé de l'option pour un select, date formatée, etc. */
const formatValeur = (def: InputSearchFieldDef, valeur: any): string => {
    if (Array.isArray(valeur)) {
        return valeur.map((v) => formatValeur(def, v)).join(", ");
    }
    if (dayjs.isDayjs(valeur)) {
        return valeur.format(APPLICATION_DATE_FORMAT);
    }
    const option = def.selectOptions?.find((o) => o.value === valeur);
    return String(option?.label ?? valeur);
};

const estRenseigne = (valeur: any): boolean =>
    valeur !== undefined && valeur !== null && valeur !== "" && (!Array.isArray(valeur) || valeur.length > 0);

/**
 * Bandeau de filtres horizontal, pleine largeur, repliable — au-dessus des résultats.
 *
 * Remplace le panneau latéral de 400px qui amputait la largeur du tableau en permanence et le
 * condamnait au défilement horizontal. Le tableau occupe désormais toute la largeur ; replier le
 * bandeau ne libère que de la hauteur, ce qui est un bonus et non la raison d'être du composant.
 *
 * Replié, les filtres renseignés restent visibles sous forme de puces retirables : sans cela, on
 * masquerait des critères qui continuent de s'appliquer.
 */
export const AdminFilterPanel: FunctionComponent<AdminFilterPanelProps> = ({
    form,
    filterDefs,
    ignoredNames = [],
    onClearFilter,
    children,
}) => {
    const { token } = theme.useToken();
    const [deplie, setDeplie] = useState<boolean>(true);

    // useWatch et non getFieldsValue : sans lui, les puces resteraient périmées à la saisie.
    const valeurs = Form.useWatch([], form) ?? {};

    const filtresActifs = filterDefs
        .filter((def) => !ignoredNames.includes(def.name) && estRenseigne(valeurs[def.name]))
        .map((def) => ({ def, libelle: formatValeur(def, valeurs[def.name]) }));

    return (
        <div
            style={{
                background: token.colorBgContainer,
                borderRadius: token.borderRadiusLG,
                border: `1px solid ${token.colorBorder}`,
                overflow: "hidden",
                boxShadow: token.boxShadowSecondary,
                marginBottom: 16,
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 16px",
                    background: "#001529",
                }}
            >
                <SearchOutlined style={{ color: "#fff", fontSize: 14 }} />
                <Text strong style={{ color: "#fff", fontSize: 14 }}>
                    Filtres de recherche
                </Text>
                {filtresActifs.length > 0 && (
                    <Badge
                        count={filtresActifs.length}
                        style={{ backgroundColor: "#1890ff" }}
                        {...({ testid: "nb-filtres-actifs" } as any)}
                    />
                )}
                <div style={{ flex: 1 }} />
                <Button
                    type="text"
                    size="small"
                    icon={deplie ? <UpOutlined /> : <DownOutlined />}
                    onClick={() => setDeplie((v) => !v)}
                    style={{ color: "#fff" }}
                    {...({ testid: "basculer-filtres" } as any)}
                >
                    {deplie ? "Réduire" : "Afficher"}
                </Button>
            </div>

            {deplie ? (
                <div style={{ padding: "16px 20px 12px 20px" }}>{children}</div>
            ) : (
                filtresActifs.length > 0 && (
                    <div style={{ padding: "10px 16px", display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {filtresActifs.map(({ def, libelle }) => (
                            <Tag
                                key={def.name}
                                closable
                                onClose={(e) => {
                                    e.preventDefault();
                                    onClearFilter(def.name);
                                }}
                                style={{ borderRadius: 4, marginInlineEnd: 0 }}
                            >
                                <strong>{def.libelle}</strong> : {libelle}
                            </Tag>
                        ))}
                    </div>
                )
            )}
        </div>
    );
};
